import * as React from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { useCreateOrder, useRetryOrder } from "@/lib/queries"
import {
  createOrderErrorMessage,
  createOrderTimeoutErrorMessage,
  isRecoverableOrderCreateError,
  readCreateOrderUnavailable,
} from "@/features/marketplace/lib/order-errors"
import {
  pollSentScriptsConfirmation,
  type SentScriptsPollResult,
} from "@/features/marketplace/lib/poll-sent-scripts-confirmation"

import type { PartnerSend } from "@/features/marketplace/lib/partner-send-plan"
import type {
  CreateOrderPayload,
  CreateOrderResult,
  MarketplaceOrderStatus,
} from "@/features/marketplace/types/marketplace.types"

export type PartnerSendOutcome = "sent" | "pending" | "failed"

export interface PartnerSendResult {
  send: PartnerSend
  outcome: PartnerSendOutcome
  orderId?: string
  status?: MarketplaceOrderStatus
  error?: string
}

interface UseMultiPartnerSendOptions {
  tenantId: string
  buildPayload: (send: PartnerSend) => Promise<CreateOrderPayload>
}

interface PartnerAttempt {
  send: PartnerSend
  attempt: () => Promise<PartnerSendResult | null>
}

const IN_FLIGHT_STATUSES: ReadonlySet<MarketplaceOrderStatus> = new Set(["PENDING_SEND", "SENDING"])

/**
 * Creates one order per partner, one after another, and keeps going after a
 * failure. Sequential on purpose: a mutation's per-call callbacks only fire
 * for its latest call, so overlapping creates would drop results.
 */
export function useMultiPartnerSend({ tenantId, buildPayload }: UseMultiPartnerSendOptions) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Each partner's failure shows on its own result row, so the hook-level toast would repeat it.
  const createOrder = useCreateOrder({ toastErrors: false })
  const retryOrder = useRetryOrder()
  const [results, setResults] = React.useState<PartnerSendResult[] | null>(null)
  const [isSending, setIsSending] = React.useState(false)
  const abortRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => () => abortRef.current?.abort(), [])

  function createOnce(payload: CreateOrderPayload): Promise<CreateOrderResult> {
    return new Promise((resolve, reject) => {
      createOrder.mutate(payload, { onSuccess: resolve, onError: reject })
    })
  }

  function retryOnce(orderId: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      retryOrder.mutate({ orderId, tenantId }, { onSuccess: resolve, onError: reject })
    })
  }

  function pollPartner(send: PartnerSend, signal: AbortSignal): Promise<SentScriptsPollResult> {
    return pollSentScriptsConfirmation(
      queryClient,
      tenantId,
      [...send.scriptIds, ...send.briefIds],
      signal,
      send.vendorId,
    )
  }

  function fromPoll(
    send: PartnerSend,
    polled: SentScriptsPollResult,
    fallback: PartnerSendResult,
  ): PartnerSendResult {
    if (polled.outcome === "sent") return { send, outcome: "sent", orderId: polled.orderId }
    if (polled.outcome === "pending") {
      // Still PENDING_SEND after the whole poll budget means the handoff was never queued,
      // so it needs the same retry as a FAILED order, not a "still sending" row.
      const needsRetry = polled.status === "FAILED" || polled.status === "PENDING_SEND"
      return {
        send,
        outcome: needsRetry ? "failed" : "pending",
        orderId: polled.orderId,
        status: polled.status,
        ...(needsRetry ? { error: createOrderTimeoutErrorMessage(t) } : {}),
      }
    }
    return fallback
  }

  async function createForPartner(send: PartnerSend, signal: AbortSignal): Promise<PartnerSendResult | null> {
    let payload: CreateOrderPayload
    try {
      payload = await buildPayload(send)
    } catch (err) {
      // A failed defaults fetch is this partner's failure, retryable from its row; it must not stop the batch.
      return { send, outcome: "failed", error: createOrderErrorMessage(err, t) }
    }
    if (signal.aborted) return null
    try {
      const created = await createOnce(payload)
      const orderId = created.orderId ?? created.data?.id ?? undefined
      const status = created.status ?? undefined
      if (status === "FAILED") {
        return { send, outcome: "failed", orderId, status, error: createOrderTimeoutErrorMessage(t) }
      }
      if (status && IN_FLIGHT_STATUSES.has(status)) {
        // The create only confirms the order was queued; wait for the handoff before calling it sent.
        return fromPoll(send, await pollPartner(send, signal), { send, outcome: "pending", orderId, status })
      }
      return { send, outcome: "sent", orderId, status }
    } catch (err) {
      if (!isRecoverableOrderCreateError(err)) {
        return { send, outcome: "failed", error: createOrderErrorMessage(err, t) }
      }
      const seeded = readCreateOrderUnavailable(err)
      const fallback: PartnerSendResult = seeded
        ? { send, outcome: "failed", orderId: seeded.orderId, status: seeded.status, error: createOrderTimeoutErrorMessage(t) }
        : { send, outcome: "failed", error: createOrderTimeoutErrorMessage(t) }
      return fromPoll(send, await pollPartner(send, signal), fallback)
    }
  }

  /** A failed partner that already has an order is re-handed-off, never created a second time. */
  async function retryExistingOrder(result: PartnerSendResult, signal: AbortSignal): Promise<PartnerSendResult> {
    try {
      await retryOnce(result.orderId!)
    } catch (err) {
      return { ...result, error: createOrderErrorMessage(err, t) }
    }
    return fromPoll(result.send, await pollPartner(result.send, signal), result)
  }

  async function run(attempts: readonly PartnerAttempt[]) {
    abortRef.current?.abort()
    const abort = new AbortController()
    abortRef.current = abort
    setIsSending(true)
    const settled: PartnerSendResult[] = []
    try {
      for (const { send, attempt } of attempts) {
        const result = await attempt().catch(
          (err: unknown): PartnerSendResult => ({ send, outcome: "failed", error: createOrderErrorMessage(err, t) }),
        )
        if (abort.signal.aborted || !result) return
        settled.push(result)
      }
    } finally {
      // Always clear: a stuck isSending blocks Cancel and closing, and the user would have to reload.
      if (!abort.signal.aborted) {
        setIsSending(false)
        setResults((previous) => mergeResults(previous, settled))
      }
    }
  }

  function sendAll(sends: readonly PartnerSend[]) {
    void run(sends.map((send) => ({ send, attempt: () => createForPartner(send, abortRef.current!.signal) })))
  }

  /** Re-sends only to partners that failed; partners that got the order are left alone. */
  function retryFailed() {
    const failed = (results ?? []).filter((r) => r.outcome === "failed")
    if (failed.length === 0) return
    void run(
      failed.map((result) => ({
        send: result.send,
        attempt: () =>
          result.orderId
            ? retryExistingOrder(result, abortRef.current!.signal)
            : createForPartner(result.send, abortRef.current!.signal),
      })),
    )
  }

  function reset() {
    abortRef.current?.abort()
    setIsSending(false)
    setResults(null)
  }

  return { results, isSending, sendAll, retryFailed, reset }
}

function mergeResults(
  previous: PartnerSendResult[] | null,
  settled: PartnerSendResult[],
): PartnerSendResult[] {
  if (!previous) return settled
  const byConnection = new Map(settled.map((r) => [r.send.connectionId, r]))
  return previous.map((r) => byConnection.get(r.send.connectionId) ?? r)
}
